import { useState, useCallback, useEffect } from 'react';
import JSZip from 'jszip';
import { ImageFile, ResizeSettings, ProcessingProgress, getFileCategory, calculateCompressionRatio, SUPPORTED_IMAGE_FORMATS, BACKGROUND_REMOVAL_FORMATS } from '../types';
import { removeBackground } from '@imgly/background-removal';

// Função para carregar configurações salvas
const loadSavedSettings = (): ResizeSettings => {
  try {
    const saved = localStorage.getItem('converterPro_imageSettings');
    if (saved) {
      const parsedSettings = JSON.parse(saved);
      // Garantir que removeBackground seja false por padrão
      return { ...parsedSettings, removeBackground: false };
    }
  } catch (error) {
    console.error('Erro ao carregar configurações:', error);
  }
  
  return {
    width: 800,
    height: 600,
    maintainAspectRatio: false,
    quality: 0.9,
    format: 'original',
    removeBackground: false
  };
};

// Função para carregar imagens salvas
const loadSavedImages = (): ImageFile[] => {
  try {
    const saved = localStorage.getItem('converterPro_sessionImages');
    if (saved) {
      const parsedImages = JSON.parse(saved);
      // Filtrar apenas metadados, não os blobs/files que não podem ser serializados
      return parsedImages.map((img: any) => ({
        ...img,
        file: null, // Arquivo não pode ser persistido
        resizedBlob: null, // Blob processado não pode ser persistido
        status: 'pending' // Reset status para pending
      })).filter((img: any) => img.originalSize); // Manter apenas imagens com dados válidos
    }
  } catch (error) {
    console.error('Erro ao carregar imagens da sessão:', error);
  }
  
  return [];
};

export function useImageResizer() {
  const [images, setImages] = useState<ImageFile[]>(loadSavedImages);
  const [settings, setSettings] = useState<ResizeSettings>(loadSavedSettings);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<ProcessingProgress>({ current: 0, total: 0 });

  // Função para calcular dimensões alvo baseadas nas configurações
  const calculateTargetDimensions = useCallback((
    originalWidth: number, 
    originalHeight: number, 
    targetSettings: ResizeSettings
  ) => {
    let { width, height, maintainAspectRatio } = targetSettings;

    if (maintainAspectRatio && originalWidth > 0 && originalHeight > 0) {
      const aspectRatio = originalWidth / originalHeight;
      // Se a largura for o fator limitante
      if (width / height > aspectRatio) {
        width = height * aspectRatio;
      } else { // Se a altura for o fator limitante
        height = width / aspectRatio;
      }
    }
    
    return {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height))
    };
  }, []);

  // Persistir configurações sempre que mudarem
  useEffect(() => {
    try {
      localStorage.setItem('converterPro_imageSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  }, [settings]);

  // Persistir metadados das imagens sempre que mudarem (sem files/blobs)
  useEffect(() => {
    try {
      const imagesToSave = images.map(img => ({
        id: img.id,
        preview: img.preview,
        originalSize: img.originalSize,
        status: img.status,
        fileType: img.fileType,
        processedAt: img.processedAt,
        compressionRatio: img.compressionRatio,
        outputSize: img.outputSize,
        individualSettings: img.individualSettings,
        fileName: img.file?.name || 'unknown', // Salvar nome do arquivo para referência
        fileSize: img.file?.size || 0,
        fileMimeType: img.file?.type || ''
      }));
      
      localStorage.setItem('converterPro_sessionImages', JSON.stringify(imagesToSave));
    } catch (error) {
      console.error('Erro ao salvar imagens da sessão:', error);
    }
  }, [images]);

  // Atualizar dimensões alvo de imagens SEM configurações individuais quando settings globais mudarem
  useEffect(() => {
    setImages(prevImages => 
      prevImages.map(img => {
        // Se a imagem tem configurações individuais, não a altere
        if (img.individualSettings) {
          return img;
        }

        // Caso contrário, recalcule com as configurações globais
        const targetDimensions = calculateTargetDimensions(
          img.originalSize.width,
          img.originalSize.height,
          settings
        );

        return {
          ...img,
          targetSize: targetDimensions,
          // Resetar status se as configs mudaram
          status: img.status === 'completed' ? 'pending' : img.status,
          resizedBlob: undefined,
        };
      })
    );
  }, [settings, calculateTargetDimensions]);

  const createImagePreview = useCallback((file: File): Promise<{ preview: string; size: { width: number; height:number } }> => {
    return new Promise((resolve, reject) => {
      if (file.type === 'image/svg+xml') {
        const reader = new FileReader();
        reader.onload = () => {
          const svgContent = reader.result as string;
          
          // Tentar extrair dimensões do SVG
          try {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');
            let width = 0, height = 0;
            
            if (svgElement) {
              const svgWidth = svgElement.getAttribute('width');
              const svgHeight = svgElement.getAttribute('height');
              const viewBox = svgElement.getAttribute('viewBox');
              
              if (svgWidth && svgHeight) {
                width = parseFloat(svgWidth.replace(/[^\d.-]/g, '')) || 0;
                height = parseFloat(svgHeight.replace(/[^\d.-]/g, '')) || 0;
              } else if (viewBox) {
                const viewBoxParts = viewBox.split(/[\s,]+/).map(Number);
                if (viewBoxParts.length >= 4) {
                  const [, , vbWidth, vbHeight] = viewBoxParts;
                  width = vbWidth || 0;
                  height = vbHeight || 0;
                }
              }
            }
            
            // Criar preview como data URL para exibição
            const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgContent);
            
            resolve({
              preview: svgDataUrl,
              size: { width, height }
            });
          } catch (error) {
            console.error('Erro ao processar preview SVG:', error);
            // Fallback: usar data URL básico
            const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgContent);
            resolve({
              preview: svgDataUrl,
              size: { width: 0, height: 0 }
            });
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Erro ao ler arquivo SVG'));
        };
        
        reader.readAsText(file);
      } else {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        img.onload = () => {
          try {
            canvas.width = Math.min(img.width, 200);
            canvas.height = Math.min(img.height, 150);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            resolve({
              preview: canvas.toDataURL(),
              size: { width: img.width, height: img.height }
            });
          } catch (error) {
            reject(new Error('Erro ao processar preview da imagem'));
          }
        };
        
        img.onerror = () => {
          reject(new Error('Erro ao carregar imagem'));
        };
        
        img.src = URL.createObjectURL(file);
      }
    });
  }, []);

  const handleFileSelect = useCallback(async (files: FileList) => {
    // Filtrar apenas arquivos de imagem
    const validFiles = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      return SUPPORTED_IMAGE_FORMATS.includes(extension as any) || 
             file.type.startsWith('image/');
    });

    if (validFiles.length === 0) {
      alert('Por favor, selecione apenas arquivos de imagem válidos.');
      return;
    }

    const newImages: ImageFile[] = [];
    
    for (const file of validFiles) {
      try {
        const { preview, size } = await createImagePreview(file);
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        const fileCategory = getFileCategory(extension);
        
        // Calcular dimensões alvo baseadas nas configurações atuais
        const targetDimensions = calculateTargetDimensions(size.width, size.height, settings);
        
        newImages.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          preview,
          originalSize: size,
          targetSize: targetDimensions,
          status: 'pending',
          fileType: fileCategory,
          processedAt: undefined,
          compressionRatio: undefined,
          outputSize: undefined
        });
      } catch (error) {
        console.error('Erro ao processar arquivo:', file.name, error);
      }
    }

    setImages(prev => [...prev, ...newImages]);
  }, [createImagePreview, calculateTargetDimensions, settings]);

  const processImageWithBackground = async (file: File, imageSettings: ResizeSettings): Promise<Blob> => {
    try {
      let processedImage: Blob | File = file;
      
      // Se a remoção de fundo estiver ativada e o formato for suportado
      if (imageSettings.removeBackground && BACKGROUND_REMOVAL_FORMATS.includes(imageSettings.format as any)) {
        console.log('🎨 Iniciando remoção avançada de fundo...');
        
        // Configurações avançadas para remoção de fundo
        const bgRemovalOptions = {
          model: "isnet" as const, // Modelo mais preciso
          output: {
            format: "image/png" as const,
            quality: 1.0,
          },
          progress: (status: string, current: number, total: number) => {
            console.log(`Status: ${status} - Progresso da remoção de fundo: ${Math.round((current/total) * 100)}%`);
          }
        };

        // Aplicar remoção de fundo com configurações otimizadas
        processedImage = await removeBackground(file, bgRemovalOptions);
        console.log('✅ Remoção de fundo concluída com sucesso!');
      }

      // Criar canvas e contexto
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { 
        alpha: true,
        willReadFrequently: true,
        desynchronized: false
      })!;
      
      // Carregar imagem processada com configurações otimizadas
      const img = await createImageBitmap(processedImage, {
        imageOrientation: 'from-image',
        premultiplyAlpha: 'premultiply',
        colorSpaceConversion: 'default'
      });
      
      // Calcular dimensões
      const targetDimensions = calculateTargetDimensions(
        img.width,
        img.height,
        imageSettings
      );

      // Configurar canvas com alta precisão
      canvas.width = targetDimensions.width;
      canvas.height = targetDimensions.height;

      // Limpar canvas e garantir transparência
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Configurar qualidade de renderização
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Aplicar otimizações de renderização
      if (imageSettings.removeBackground) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.filter = 'none'; // Evitar filtros que possam afetar a transparência
      }

      // Desenhar imagem com alta qualidade
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Pós-processamento para remover resquícios de fundo
      if (imageSettings.removeBackground) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        
        // Limpar pixels semi-transparentes com threshold mais agressivo
        for (let i = 0; i < pixels.length; i += 4) {
          const alpha = pixels[i + 3];
          if (alpha < 252) { // Threshold mais alto para maior precisão
            // Análise de contexto para melhor detecção de bordas
            const isEdge = alpha > 200 && (
              i > 0 && pixels[i - 1] > 240 || // Verificar pixels vizinhos
              i < pixels.length - 4 && pixels[i + 4] > 240
            );
            
            if (!isEdge) {
              pixels[i + 3] = alpha < 30 ? 0 : alpha; // Mais agressivo na remoção
            }
          }
        }
        
        ctx.putImageData(imageData, 0, 0);
      }

      // Converter para blob com configurações otimizadas
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob!),
          `image/${imageSettings.format === 'jpg' ? 'jpeg' : imageSettings.format}`,
          1.0 // Máxima qualidade para preservar transparência
        );
      });
    } catch (error) {
      console.error('❌ Erro ao processar imagem:', error);
      throw error;
    }
  };

  const processImage = useCallback(async (imageFile: ImageFile) => {
    if (!imageFile.file || imageFile.status === 'processing') {
      console.warn('⚠️ Imagem inválida ou já em processamento:', imageFile);
      return;
    }

    console.log('🔄 Processando imagem individual:', imageFile.file.name);
    
    try {
      // Atualizar status para processando
      setImages(prev => prev.map(img => 
        img.id === imageFile.id ? { ...img, status: 'processing' } : img
      ));

      // Usar configurações individuais se existirem, caso contrário usar globais
      const imageSettings = imageFile.individualSettings 
        ? { 
            ...settings, // Usar configurações globais como base
            ...imageFile.individualSettings, // Sobrescrever com configurações individuais
            maintainAspectRatio: settings.maintainAspectRatio // Manter a proporção das configurações globais
          }
        : settings;

      // Processar imagem com remoção de fundo se necessário
      const processedBlob = await processImageWithBackground(imageFile.file, imageSettings);
      
      // Criar arquivo processado
      const processedFile = new File([processedBlob], 
        `${imageFile.file.name.split('.')[0]}.${imageSettings.format}`, 
        { type: processedBlob.type }
      );
      
      // Calcular taxa de compressão
      const compressionRatio = calculateCompressionRatio(imageFile.file.size, processedBlob.size);
      
      // Atualizar imagem com resultado
      setImages(prev => prev.map(img => 
        img.id === imageFile.id ? {
          ...img,
          status: 'completed',
          processedFile,
          resizedBlob: processedBlob,
          compressionRatio,
          outputSize: processedBlob.size,
          processedAt: new Date()
        } : img
      ));
      
      console.log(`✅ Imagem processada: ${imageFile.file.name}`);
      console.log(`📊 Taxa de compressão: ${compressionRatio}%`);
      
    } catch (error) {
      console.error(`❌ Erro ao processar imagem ${imageFile.file.name}:`, error);
      
      // Marcar imagem como erro
      setImages(prev => prev.map(img => 
        img.id === imageFile.id ? { ...img, status: 'error' } : img
      ));
    }
  }, [settings, processImageWithBackground]);

  const processImages = useCallback(async (imagesToProcess: ImageFile[]) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      const total = imagesToProcess.length;
      let processed = 0;
      
      for (const image of imagesToProcess) {
        if (!image.file) continue;
        
        try {
          // Usar configurações individuais se existirem, caso contrário usar globais
          const imageSettings = image.individualSettings 
            ? { 
                ...settings, // Usar configurações globais como base
                ...image.individualSettings, // Sobrescrever com configurações individuais
                maintainAspectRatio: settings.maintainAspectRatio // Manter a proporção das configurações globais
              }
            : settings;
          
          console.log(`🔄 Processando imagem: ${image.file.name}`);
          console.log('⚙️ Configurações:', imageSettings);
          
          // Atualizar status para processando
          setImages(prev => prev.map(img => 
            img.id === image.id ? { ...img, status: 'processing' } : img
          ));
          
          // Processar imagem com remoção de fundo se necessário
          const processedBlob = await processImageWithBackground(image.file, imageSettings);
          
          // Criar arquivo processado
          const processedFile = new File([processedBlob], 
            `${image.file.name.split('.')[0]}.${imageSettings.format}`, 
            { type: processedBlob.type }
          );
          
          // Calcular taxa de compressão
          const compressionRatio = calculateCompressionRatio(image.file.size, processedBlob.size);
          
          // Atualizar imagem com resultado
          setImages(prev => prev.map(img => 
            img.id === image.id ? {
              ...img,
              status: 'completed',
              processedFile,
              resizedBlob: processedBlob,
              compressionRatio,
              outputSize: processedBlob.size,
              processedAt: new Date()
            } : img
          ));
          
          processed++;
          setProgress({ current: processed, total });
          
          console.log(`✅ Imagem processada: ${image.file.name}`);
          console.log(`📊 Taxa de compressão: ${compressionRatio}%`);
          
        } catch (error) {
          console.error(`❌ Erro ao processar imagem ${image.file.name}:`, error);
          
          // Marcar imagem como erro
          setImages(prev => prev.map(img => 
            img.id === image.id ? { ...img, status: 'error' } : img
          ));
        }
      }
    } finally {
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [isProcessing, settings, processImageWithBackground, calculateTargetDimensions]);

  const downloadImage = useCallback((imageFile: ImageFile) => {
    if (!imageFile.resizedBlob) return;

    const url = URL.createObjectURL(imageFile.resizedBlob);
    const a = document.createElement('a');
    a.href = url;
    
    // Determinar a extensão correta baseada nas configurações individuais ou globais
    const effectiveFormat = imageFile.individualSettings?.format || settings.format;
    let extension: string;
    
    if (effectiveFormat === 'original') {
      // Para SVG original convertido, usar PNG como padrão
      if (imageFile.file.type === 'image/svg+xml') {
        extension = 'png';
      } else {
        extension = imageFile.file.name.split('.').pop() || 'jpg';
      }
    } else {
      // Mapear formatos para extensões corretas
      const extensionMap: { [key: string]: string } = {
        'jpeg': 'jpg',
        'jpg': 'jpg',
        'png': 'png',
        'webp': 'webp',
        'gif': 'gif',
        'bmp': 'bmp',
        'tiff': 'tiff',
        'tif': 'tif',
        'avif': 'avif',
        'heic': 'heic',
        'heif': 'heif',
        'ico': 'ico',
        'svg': 'svg',
        'psd': 'psd',
        'dds': 'dds',
        'hdr': 'hdr',
        'exr': 'exr',
        'tga': 'tga',
        'pcx': 'pcx',
        'jp2': 'jp2',
        'jbig': 'jbig',
        'pbm': 'pbm',
        'pgm': 'pgm',
        'ppm': 'ppm',
        'xbm': 'xbm',
        'xpm': 'xpm',
        'sun': 'sun',
        'sgi': 'sgi',
        'ras': 'ras',
        'pct': 'pct'
      };
      
      extension = extensionMap[effectiveFormat] || effectiveFormat;
    }
    
    const baseName = imageFile.file.name.split('.')[0];
    a.download = `${baseName}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [settings.format]);

  const downloadAll = useCallback(() => {
    const completedImages = images.filter(img => img.status === 'completed');
    completedImages.forEach(img => downloadImage(img));
  }, [images, downloadImage]);

  const downloadAllAsZip = useCallback(async () => {
    const completedImages = images.filter(img => img.status === 'completed');
    if (completedImages.length === 0) return;

    const zip = new JSZip();
    
    // Adicionar cada imagem ao ZIP
    completedImages.forEach((imageFile) => {
      if (imageFile.resizedBlob) {
        // Determinar a extensão correta baseada nas configurações individuais ou globais
        const effectiveFormat = imageFile.individualSettings?.format || settings.format;
        let extension: string;
        
        if (effectiveFormat === 'original') {
          // Para SVG original convertido, usar PNG como padrão
          if (imageFile.file.type === 'image/svg+xml') {
            extension = 'png';
          } else {
            extension = imageFile.file.name.split('.').pop() || 'jpg';
          }
        } else {
          const extensionMap: { [key: string]: string } = {
            'jpeg': 'jpg',
            'jpg': 'jpg',
            'png': 'png',
            'webp': 'webp',
            'gif': 'gif',
            'bmp': 'bmp',
            'tiff': 'tiff',
            'tif': 'tif',
            'avif': 'avif',
            'heic': 'heic',
            'heif': 'heif',
            'ico': 'ico',
            'svg': 'svg',
            'psd': 'psd',
            'dds': 'dds',
            'hdr': 'hdr',
            'exr': 'exr',
            'tga': 'tga',
            'pcx': 'pcx',
            'jp2': 'jp2',
            'jbig': 'jbig',
            'pbm': 'pbm',
            'pgm': 'pgm',
            'ppm': 'ppm',
            'xbm': 'xbm',
            'xpm': 'xpm',
            'sun': 'sun',
            'sgi': 'sgi',
            'ras': 'ras',
            'pct': 'pct'
          };
          
          extension = extensionMap[effectiveFormat] || effectiveFormat;
        }
        
        const baseName = imageFile.file.name.split('.')[0];
        const fileName = `${baseName}.${extension}`;
        zip.file(fileName, imageFile.resizedBlob);
      }
    });

    // Gerar e baixar o ZIP
    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `imagens.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao criar ZIP:', error);
    }
  }, [images, settings.format]);

  const removeImage = useCallback((id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setImages([]);
  }, []);

  const updateIndividualSettings = useCallback((id: string, newSettings: Partial<NonNullable<ImageFile['individualSettings']>>) => {
    setImages(prevImages =>
      prevImages.map(img => {
        if (img.id === id) {
          const existingSettings = img.individualSettings || {
            format: settings.format,
            quality: settings.quality,
            width: settings.width,
            height: settings.height,
          };

          const updatedSettings = { ...existingSettings, ...newSettings };
          
          // Se o formato for 'original', não há qualidade a ser definida
          if (updatedSettings.format === 'original') {
            // A qualidade ainda é mantida no objeto para consistência de tipo,
            // mas não será usada na conversão se a lógica downstream a ignorar.
          }
          
          // Recalcula as dimensões alvo para esta imagem específica
          const effectiveResizeSettings: ResizeSettings = {
            width: updatedSettings.width ?? settings.width,
            height: updatedSettings.height ?? settings.height,
            quality: updatedSettings.quality,
            format: updatedSettings.format,
            maintainAspectRatio: settings.maintainAspectRatio,
            removeBackground: settings.removeBackground,
          };

          const targetDimensions = calculateTargetDimensions(
            img.originalSize.width,
            img.originalSize.height,
            effectiveResizeSettings
          );
          
          return {
            ...img,
            individualSettings: updatedSettings,
            targetSize: targetDimensions,
            status: 'pending', // Resetar o status para reconverter
            resizedBlob: undefined
          };
        }
        return img;
      })
    );
  }, [settings, calculateTargetDimensions]);

  const onUpdateSettings = useCallback((key: keyof ResizeSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  return {
    images,
    settings,
    isProcessing,
    progress,
    handleFileSelect,
    processImage,
    processImages,
    downloadImage,
    downloadAll,
    downloadAllAsZip,
    removeImage,
    clearAll,
    updateIndividualSettings,
    onUpdateSettings,
    processImageWithBackground
  };
} 